import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Autocomplete, TextField, Button, Alert, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip, ToggleButtonGroup, ToggleButton,
  CircularProgress, useTheme, Chip, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import TuneIcon from '@mui/icons-material/Tune';
import HandshakeIcon from '@mui/icons-material/Handshake';
import BulkMappingWizard from './BulkMappingWizard';
import ApiService from '../../services/api.service';
import useOrganizationSearch from '../../hooks/useOrganizationSearch';
import internalServiceService from '../../services/internal-service.service';
import serviceFieldService from '../../services/service-field.service';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import { can } from '../../utils/roleUtils';
import { commissionService } from '../../services/accounting/commission.service';
import { serverMessage } from '../../utils/apiConvert';

const apiErr = (err, fallback) =>
  err?.response?.data?.error || err?.response?.data?.message || fallback;

const money = (v) => (v == null ? '—' : Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const RECIPIENT_SHORT = { EXTERNAL_AGENT: 'Agent', INTERNAL_STAFF: 'Staff', EXTERNAL_PRO: 'PRO' };
const ruleRate = (r) => (r.rateType === 'PERCENTAGE' ? `${Number(r.rateValue)}%` : `AED ${Number(r.rateValue).toFixed(2)}`);
// Total rate when every rule is the same kind (all % or all fixed); null when they mix.
const totalRate = (rs) => {
  if (rs.every((r) => r.rateType === 'PERCENTAGE')) return `${rs.reduce((t, r) => t + Number(r.rateValue), 0)}%`;
  if (rs.every((r) => r.rateType !== 'PERCENTAGE')) return `AED ${rs.reduce((t, r) => t + Number(r.rateValue), 0).toFixed(2)}`;
  return null;
};

const CommissionCell = ({ rules: rs }) => {
  if (rs.length === 0) return <Typography component="span" sx={{ fontSize: '0.8rem', color: 'text.disabled' }}>—</Typography>;
  const total = totalRate(rs);
  return (
    <Tooltip title={rs.map((r) => `${r.recipient?.name || 'Agent'} — ${ruleRate(r)}`).join(' · ')}>
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
        {total && <Typography component="span" sx={{ fontSize: '0.8rem' }}>{total}</Typography>}
        <Chip label={`${rs.length} ${rs.length === 1 ? 'agent' : 'agents'}`} size="small"
          sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }} />
      </Box>
    </Tooltip>
  );
};

const EMPTY_RULE = { recipientId: '', rateType: 'PERCENTAGE', rateValue: '' };

// ─── Commission Dialog (v3) ─────────────────────────────────────────────────
// The commission rules for one customer × service; Save creates the added rules and deletes the removed ones.
const CommissionDialog = ({ open, onClose, mapping, orgName, rules, recipients, onSaved }) => {
  const theme = useTheme();
  const [list, setList] = useState([]);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(EMPTY_RULE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const hasRecipients = recipients && recipients.length > 0;

  const rulesKey = rules.map((r) => `${r.id}:${r.rateType}:${r.rateValue}:${r.active}`).join('|');
  useEffect(() => {
    if (!open) return;
    setList(rules.map((r) => ({ ...r, recipientId: r.recipient?.id, recipientName: r.recipient?.name, recipientType: r.recipient?.type })));
    setAdding(false);
    setDraft(EMPTY_RULE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mapping?.id, rulesKey]);
  useEffect(() => { if (open) setError(''); }, [open, mapping?.id]);

  const available = recipients.filter((r) => r.active !== false && !list.some((x) => x.recipientId === r.id));
  const validDraft = draft.recipientId && Number(draft.rateValue) > 0 && /^\d+(\.\d{1,2})?$/.test(String(draft.rateValue))
    && (draft.rateType !== 'PERCENTAGE' || Number(draft.rateValue) <= 100);
  const addDraft = () => {
    const r = recipients.find((x) => x.id === draft.recipientId);
    setList((l) => [...l, { ...draft, recipientName: r?.name, recipientType: r?.type }]);
    setDraft(EMPTY_RULE);
    setAdding(false);
  };

  const save = async () => {
    setSaving(true);
    setError('');
    const failed = [];
    for (const r of rules.filter((x) => !list.some((y) => y.id === x.id))) {
      try { await commissionService.deleteRule(r.id); } catch (err) { failed.push(`${r.recipient?.name}: ${serverMessage(err, 'not removed')}`); }
    }
    for (const r of list.filter((x) => !x.id)) {
      try {
        await commissionService.createRule({
          internalOrganizationId: mapping.internal_organization_id, internalServiceId: mapping.internal_service_id,
          recipientId: r.recipientId, rateType: r.rateType, rateValue: r.rateValue,
        });
      } catch (err) { failed.push(`${r.recipientName}: ${serverMessage(err, 'not added')}`); }
    }
    setSaving(false);
    await onSaved();
    if (failed.length) { setError(failed.join(' · ')); return; }
    onClose();
  };

  const inputSx = { '& .MuiInputBase-root': { fontSize: '0.8rem', bgcolor: 'background.paper' } };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: '0.95rem', fontWeight: 600, pb: 1 }}>
        Commission — {mapping?.organization_name || orgName || 'Client'} × {mapping?.service_name || 'Service'}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1.5 }}>
          Agents and staff who earn commission on this mapping. Rate applied to Service Fee only.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.75rem' }} onClose={() => setError('')}>{error}</Alert>}

        {list.length === 0 && !adding && (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', mb: 1.5 }}>
            <Typography color="text.secondary" sx={{ fontSize: '0.8rem', mb: 1 }}>
              {!hasRecipients ? 'No commission recipients configured' : 'No commission rules configured'}
            </Typography>
            {!hasRecipients ? (
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 2 }}>
                First, set up commission recipients under <br /> <strong>Commissions → Recipients</strong>
              </Typography>
            ) : (
              <Button size="small" variant="contained" onClick={() => setAdding(true)} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                + Add Agent
              </Button>
            )}
          </Paper>
        )}

        {list.length > 0 && (
          <Table size="small" sx={{ mb: 1.5 }}>
            <TableHead sx={getBrandedTableHeadSx(theme)}>
              <TableRow sx={{ '& th': { fontSize: '0.75rem' } }}>
                <TableCell>Recipient</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Rate</TableCell>
                <TableCell align="right" width={50} />
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((rule) => (
                <TableRow key={rule.id || rule.recipientId} sx={{ '& td': { fontSize: '0.8rem' } }}>
                  <TableCell>
                    {rule.recipientName}
                    <Typography component="span" sx={{ fontSize: '0.65rem', color: 'text.secondary', ml: 0.75 }}>
                      ({RECIPIENT_SHORT[rule.recipientType] || 'Agent'})
                    </Typography>
                    {rule.active === false && (
                      <Typography component="span" sx={{ fontSize: '0.65rem', color: 'warning.main', ml: 0.75 }}>inactive</Typography>
                    )}
                  </TableCell>
                  <TableCell>{rule.rateType === 'PERCENTAGE' ? '% of SC' : 'Fixed'}</TableCell>
                  <TableCell align="right">{ruleRate(rule)}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="error" onClick={() => setList((l) => l.filter((x) => x !== rule))}>
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {adding ? (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 1, bgcolor: 'action.hover', p: 1, borderRadius: 1 }}>
            <Box sx={{ flex: 2 }}>
              <TextField
                size="small"
                select
                value={draft.recipientId}
                label="Select recipient"
                fullWidth
                sx={{ ...inputSx }}
                onChange={(e) => setDraft((d) => ({ ...d, recipientId: e.target.value }))}
              >
                {available.length === 0 ? (
                  <MenuItem disabled sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                    No recipients available
                  </MenuItem>
                ) : (
                  available.map((r) => (
                    <MenuItem key={r.id} value={r.id} sx={{ fontSize: '0.8rem' }}>
                      {r.name} <Typography component="span" sx={{ fontSize: '0.65rem', color: 'text.secondary', ml: 0.75 }}>· {RECIPIENT_SHORT[r.type]}</Typography>
                    </MenuItem>
                  ))
                )}
              </TextField>
              {available.length === 0 && (
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.5 }}>
                  Add recipients under Commissions → Recipients
                </Typography>
              )}
            </Box>
            <TextField
              size="small"
              select
              value={draft.rateType}
              sx={{ flex: 1, ...inputSx }}
              onChange={(e) => setDraft((d) => ({ ...d, rateType: e.target.value }))}
            >
              <MenuItem value="PERCENTAGE">% of SC</MenuItem>
              <MenuItem value="FIXED">Fixed AED</MenuItem>
            </TextField>
            <TextField
              size="small"
              type="number"
              placeholder={draft.rateType === 'PERCENTAGE' ? '%' : 'AED'}
              value={draft.rateValue}
              onChange={(e) => setDraft((d) => ({ ...d, rateValue: e.target.value }))}
              inputProps={{ min: 0, step: '0.01' }}
              sx={{ width: 80, ...inputSx }}
            />
            <Button
              size="small"
              variant="contained"
              sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 50, mt: 0.25 }}
              disabled={!validDraft || !hasRecipients}
              onClick={addDraft}
            >
              Add
            </Button>
            <Button
              size="small"
              sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 0, color: 'text.secondary', mt: 0.25 }}
              onClick={() => { setAdding(false); setDraft(EMPTY_RULE); }}
            >
              ✕
            </Button>
          </Box>
        ) : list.length > 0 && (
          <Button size="small" onClick={() => setAdding(true)} sx={{ textTransform: 'none', fontSize: '0.75rem', mt: 0.5 }}>
            + Add another agent
          </Button>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving} sx={{ textTransform: 'none' }}>{saving ? 'Saving…' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Field Defaults Dialog ──────────────────────────────────────────────────
// Set per-organization default values for a service's custom fields.
const FieldDefaultsDialog = ({ open, onClose, serviceId, serviceName, orgId, orgName, readOnly = false }) => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (open && serviceId && orgId) {
      setLoading(true);
      setSuccess('');
      setSaveError('');
      serviceFieldService.getDefaults(serviceId, orgId)
        .then(data => {
          setFields(data.map(f => ({
            fieldId: f.fieldId,
            code: f.code,
            label: f.label,
            fieldType: f.fieldType || f.field_type || 'TEXT',
            required: f.required,
            serviceDefault: f.serviceDefault || f.service_default || '',
            options: f.options || [],
            placeholder: f.placeholder || '',
            orgDefault: f.orgDefault || f.org_default || ''
          })));
        })
        .catch(() => setFields([]))
        .finally(() => setLoading(false));
    }
  }, [open, serviceId, orgId]);

  const handleChange = (idx, value) => {
    setFields(prev => prev.map((f, i) => i === idx ? { ...f, orgDefault: value } : f));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      await serviceFieldService.saveDefaults(serviceId, orgId,
        fields.map(f => ({ fieldId: f.fieldId, defaultValue: f.orgDefault }))
      );
      setSuccess('Defaults saved');
      setTimeout(() => onClose(), 800);
    } catch (err) {
      console.error('Error saving field defaults:', err);
      setSaveError(apiErr(err, 'Failed to save field defaults'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
        Field Defaults
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Service</Typography>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{serviceName}</Typography>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.5 }}>Client</Typography>
          <Typography sx={{ fontSize: '0.75rem' }}>{orgName}</Typography>
        </Box>

        {success && <Alert severity="success" sx={{ mb: 1 }}>{success}</Alert>}
        {saveError && <Alert severity="error" sx={{ mb: 1 }}>{saveError}</Alert>}

        {loading ? (
          <Box textAlign="center" py={3}><CircularProgress size={24} /></Box>
        ) : fields.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center', fontSize: '0.85rem' }}>
            No custom fields defined for this service
          </Typography>
        ) : (
          fields.map((f, idx) => (
            <Box key={f.fieldId} sx={{ mb: 1.5 }}>
              {f.fieldType === 'SELECT' ? (
                <TextField
                  select fullWidth size="small"
                  label={f.label + (f.required ? ' *' : '')}
                  value={f.orgDefault || ''}
                  disabled={readOnly}
                  onChange={(e) => handleChange(idx, e.target.value)}
                  helperText={f.serviceDefault ? `Service default: ${f.serviceDefault}` : undefined}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {(f.options || []).map((opt, i) => (
                    <MenuItem key={i} value={typeof opt === 'string' ? opt : (opt.value || '')}>
                      {typeof opt === 'string' ? opt : (opt.label || opt.value || '')}
                    </MenuItem>
                  ))}
                </TextField>
              ) : f.fieldType === 'DATE' ? (
                <TextField
                  fullWidth size="small" type="date"
                  label={f.label + (f.required ? ' *' : '')}
                  value={f.orgDefault || ''}
                  disabled={readOnly}
                  onChange={(e) => handleChange(idx, e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText={f.serviceDefault ? `Service default: ${f.serviceDefault}` : undefined}
                />
              ) : (
                <TextField
                  fullWidth size="small"
                  type={f.fieldType === 'NUMBER' ? 'number' : 'text'}
                  multiline={f.fieldType === 'TEXTAREA'}
                  rows={f.fieldType === 'TEXTAREA' ? 2 : undefined}
                  label={f.label + (f.required ? ' *' : '')}
                  value={f.orgDefault || ''}
                  disabled={readOnly}
                  onChange={(e) => handleChange(idx, e.target.value)}
                  placeholder={f.placeholder || f.serviceDefault || ''}
                  helperText={f.serviceDefault ? `Service default: ${f.serviceDefault}` : undefined}
                />
              )}
            </Box>
          ))
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ fontSize: '0.75rem', textTransform: 'none' }}>{readOnly ? 'Close' : 'Cancel'}</Button>
        {!readOnly && (
          <Button variant="contained" onClick={handleSave} disabled={saving || fields.length === 0}
            sx={{ fontSize: '0.75rem', textTransform: 'none' }}>
            {saving ? 'Saving...' : 'Save Defaults'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const ServiceMappings = ({ currentUser }) => {
  const theme = useTheme();
  const typingCenterId = currentUser?.typingCenterId || currentUser?.typing_center_id;

  const [mode, setMode] = useState('client'); // 'client' | 'service'
  const {
    options: orgs,
    loading: orgsLoading,
    onInputChange: onOrgInputChange,
    filterOptions: orgFilterOptions
  } = useOrganizationSearch({
    typingCenterId,
    mapOption: (o) => ({ id: o.id, name: o.name || o.organization_name, code: o.code || o.organization_code || '' })
  });
  const [services, setServices] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [bulkWizardOpen, setBulkWizardOpen] = useState(false);
  const [fieldDefaultsOpen, setFieldDefaultsOpen] = useState(false);
  const [fieldDefaultsMapping, setFieldDefaultsMapping] = useState(null);
  const canCommission = can(currentUser, 'finance', 'read');
  const canEditCommission = can(currentUser, 'finance', 'write');
  const [commRules, setCommRules] = useState([]);
  const [commRecipients, setCommRecipients] = useState([]);
  const [commMapping, setCommMapping] = useState(null);

  const loadCommission = useCallback(async () => {
    if (!canCommission) return;
    try {
      const [rules, recipients] = await Promise.all([commissionService.getRules(), commissionService.getRecipients()]);
      setCommRules(rules);
      setCommRecipients(recipients || []);
    } catch (err) {
      console.error('Error loading commission data:', err);
      setCommRules([]);
      setCommRecipients([]);
    }
  }, [canCommission]);

  useEffect(() => { loadCommission(); }, [loadCommission]);
  const rulesOf = (m) => commRules.filter((r) => r.internalOrganization?.id === m.internal_organization_id
    && r.internalServiceId === m.internal_service_id);

  useEffect(() => {
    (async () => {
      try {
        const svc = await internalServiceService.getMappableServices();
        setServices(svc);
      } catch (err) {
        setError(apiErr(err, 'Failed to load clients/services'));
      }
    })();
  }, [typingCenterId]);

  const load = useCallback(async () => {
    setError('');
    const params = mode === 'client'
      ? (selectedOrg ? { organizationId: selectedOrg.id } : null)
      : (selectedService ? { serviceId: selectedService.id } : null);
    if (!params) { setRows([]); return; }
    setLoading(true);
    try {
      setRows(await internalServiceService.listMappings(params));
    } catch (err) {
      setError(apiErr(err, 'Failed to load mappings'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [mode, selectedOrg, selectedService]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this price mapping?')) return;
    try {
      await internalServiceService.deleteMapping(id);
      await load();
    } catch (err) {
      setError(apiErr(err, 'Failed to remove mapping'));
    }
  };

  const byClient = mode === 'client';
  const search = searchTerm.trim().toLowerCase();
  const visibleRows = search
    ? rows.filter((r) => [
      byClient ? r.service_name : r.organization_name,
      byClient ? r.service_code : r.organization_code,
    ].some((v) => (v || '').toLowerCase().includes(search)))
    : rows;

  return (
    <Box sx={{ p: 0 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 0.75 }}>
          <Box>
            <ToggleButtonGroup
              exclusive size="small" value={mode}
              onChange={(e, v) => { if (v) { setMode(v); setRows([]); setSearchTerm(''); } }}
              sx={{
                '& .MuiToggleButton-root.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': { bgcolor: 'primary.dark' }
                }
              }}
            >
              <ToggleButton value="client" sx={{ fontSize: '0.75rem', px: 1.5, height: 32, textTransform: 'none' }}>By Client</ToggleButton>
              <ToggleButton value="service" sx={{ fontSize: '0.75rem', px: 1.5, height: 32, textTransform: 'none' }}>By Service</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box sx={{ flex: '1 1 260px', minWidth: 220, maxWidth: 560, '& .MuiInputBase-root': { minHeight: 32, py: '0 !important' }, '& .MuiInputBase-input': { fontSize: '0.8125rem' } }}>
            {byClient ? (
              <Autocomplete
                options={orgs}
                getOptionLabel={(o) => `${o.name}${o.code ? ` (${o.code})` : ''}`}
                value={selectedOrg}
                onChange={(e, v) => setSelectedOrg(v)}
                onInputChange={onOrgInputChange}
                filterOptions={orgFilterOptions}
                loadingText="Searching..."
                noOptionsText="No organizations found"
                loading={orgsLoading}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                ListboxProps={{ sx: { '& .MuiAutocomplete-option': { fontSize: '0.8125rem', py: 0.5, minHeight: 32 } } }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Search clients..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: '#757575', fontSize: 20, mr: 0.5 }} />
                        </InputAdornment>
                      ),
                    }}
                    autoComplete="off"
                    inputProps={{ ...params.inputProps, autoComplete: 'new-password' }}
                  />
                )}
              />
            ) : (
              <Autocomplete
                options={services}
                getOptionLabel={(s) => `${s.name}${s.code ? ` (${s.code})` : ''}`}
                value={selectedService}
                onChange={(e, v) => setSelectedService(v)}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                ListboxProps={{ sx: { '& .MuiAutocomplete-option': { fontSize: '0.8125rem', py: 0.5, minHeight: 32 } } }}
                renderInput={(params) => <TextField {...params} size="small" placeholder="Search services (mappable only)..." />}
              />
            )}
          </Box>
          {can(currentUser, 'service_catalog', 'write') && (
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setBulkWizardOpen(true)}
              sx={{ fontSize: '0.8125rem', textTransform: 'none', ml: 'auto' }}>
              Create Mapping
            </Button>
          )}
      </Box>
      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1.5 }}>
        Per-client prices for non-uniform services. Select a client or a service to view its mappings.
      </Typography>

      {/* Search Bar */}
      {(byClient ? selectedOrg : selectedService) && (
        <Paper sx={{ mb: 2 }}>
          <TextField
            fullWidth
            placeholder={byClient ? 'Search services...' : 'Search clients...'}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                p: '8px 12px',
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#757575', fontSize: 20, mr: 0.5 }} />
                </InputAdornment>
              ),
            }}
          />
        </Paper>
      )}

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead sx={getBrandedTableHeadSx(theme)}>
              <TableRow sx={{ '& th': { fontSize: '0.8rem' } }}>
                <TableCell>{byClient ? 'Service' : 'Client'}</TableCell>
                <TableCell align="right">Service Fee</TableCell>
                <TableCell align="right">Tax</TableCell>
                <TableCell align="right">Govt Fee</TableCell>
                {canCommission && <TableCell align="right">Commission</TableCell>}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={canCommission ? 6 : 5} align="center" sx={{ py: 4 }}><CircularProgress size={26} /></TableCell></TableRow>
              ) : visibleRows.length === 0 ? (
                <TableRow><TableCell colSpan={canCommission ? 6 : 5} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {(byClient ? selectedOrg : selectedService)
                      ? searchTerm.trim()
                        ? `No ${byClient ? 'services' : 'clients'} match your search.`
                        : 'No mappings.'
                      : 'Select a ' + (byClient ? 'client' : 'service') + ' above.'}
                  </Typography>
                </TableCell></TableRow>
              ) : visibleRows.map((r) => (
                <TableRow key={r.id} hover sx={{ '& td': { fontSize: '0.8rem' } }}>
                  <TableCell>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                      {byClient
                        ? `${r.service_name || ''}${r.service_code ? ` (${r.service_code})` : ''}`
                        : (r.organization_name || '—')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{money(r.service_charge)}</TableCell>
                  <TableCell align="right">{money(r.tax_on_service_charge)}</TableCell>
                  <TableCell align="right">{money(r.govt_charge)}</TableCell>
                  {canCommission && (
                    <TableCell align="right">
                      <CommissionCell rules={rulesOf(r)} />
                    </TableCell>
                  )}
                  <TableCell align="right">
                    {canEditCommission && (
                      <Tooltip title="Commission config">
                        <IconButton size="small" color="primary" onClick={() => setCommMapping(r)}>
                          <HandshakeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Field defaults">
                      <IconButton size="small" onClick={() => { setFieldDefaultsMapping(r); setFieldDefaultsOpen(true); }}>
                        <TuneIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {can(currentUser, 'service_catalog', 'delete') && (
                      <Tooltip title="Remove mapping">
                        <IconButton size="small" color="error" onClick={() => handleDelete(r.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <BulkMappingWizard
        open={bulkWizardOpen}
        onClose={() => setBulkWizardOpen(false)}
        onComplete={() => {
          // Reload current view
          load();
        }}
      />

      <CommissionDialog
        open={Boolean(commMapping)}
        onClose={() => setCommMapping(null)}
        mapping={commMapping}
        orgName={byClient ? selectedOrg?.name : ''}
        rules={commMapping ? rulesOf(commMapping) : []}
        recipients={commRecipients}
        onSaved={loadCommission}
      />

      <FieldDefaultsDialog
        readOnly={!can(currentUser, 'service_catalog', 'write')}
        open={fieldDefaultsOpen}
        onClose={() => setFieldDefaultsOpen(false)}
        serviceId={fieldDefaultsMapping?.internal_service_id}
        serviceName={fieldDefaultsMapping?.service_name || ''}
        orgId={fieldDefaultsMapping?.internal_organization_id}
        orgName={fieldDefaultsMapping?.organization_name || (byClient ? selectedOrg?.name : '')}
      />

    </Box>
  );
};

export default ServiceMappings;
